import React, { useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Box, Divider, Stack, Typography } from '@mui/material';

import { createSxProps } from 'src/components/types';

import ActivityLogContent from '../../Helpers/ActivityLogContent';

const useSxProps = () => {
  const sxProps = useMemo(
    () => ({
      metadata: {
        backgroundColor: 'background.subtle',
        px: 1.75,
        py: 0.125,
      },

      metadataLabelRow: {
        color: 'text.secondary',
        fontWeight: 600,
        fontSize: (theme) => theme.typography.caption.fontSize,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: '52px',
      },

      metadataValue: {
        color: 'text.primary',
        fontWeight: 500,
        fontSize: (theme) => theme.typography.body2.fontSize,
        wordBreak: 'break-word',
      },

      divider: {
        borderColor: 'borderState.strong',
      },

      bodySection: {
        px: 1.75,
        pt: 0.375,
      },

      body: {
        color: 'text.primary',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      },
    }),
    []
  );

  return createSxProps(sxProps);
};

interface Props {
  fromName: string;
  toName: string;
  body: string;
}

const ActivityLogFeedSlackMessage = ({ fromName, toName, body }: Props) => {
  const sxProps = useSxProps();
  const { t } = useTranslation();
  const fromLabel = t('hiring-dashboard-candidates:candidates.activityLog.from', { defaultValue: 'From:' });
  const toLabel = t('hiring-dashboard-candidates:candidates.activityLog.to', { defaultValue: 'To:' });

  const getMetadataRow = (label: string, value: string) => (
    <Stack direction="row" spacing={0} alignItems="flex-start">
      <Typography component="div" sx={sxProps.metadataLabelRow}>
        {label}
      </Typography>
      <Typography sx={sxProps.metadataValue}>{value}</Typography>
    </Stack>
  );

  return (
    <ActivityLogContent
      fullWidth
      sx={{
        borderRadius: '10px',
        px: 0,
        py: 1.5,
        borderColor: 'borderState.strong',
        overflow: 'hidden',
      }}
      content={
        <Stack direction="column" spacing={0.875}>
          <Stack direction="column" spacing={0} sx={sxProps.metadata}>
            {getMetadataRow(fromLabel, fromName)}
            {getMetadataRow(toLabel, toName)}
          </Stack>
          <Divider sx={sxProps.divider} />
          <Box sx={sxProps.bodySection}>
            <Typography sx={sxProps.body}>{body}</Typography>
          </Box>
        </Stack>
      }
    />
  );
};

export default ActivityLogFeedSlackMessage;
